import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useLang } from "../context/LanguageContext";
import { activities } from "../data/activities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Booking = () => {
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const preActivity = searchParams.get("activity") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: "2",
    activity: preActivity,
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preActivity) setForm((f) => ({ ...f, activity: preActivity }));
  }, [preActivity]);

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(t.booking.error_title, { description: t.extras.booking_pick_date_err });
      return;
    }
    if (!form.activity) {
      toast.error(t.booking.error_title, { description: t.extras.booking_pick_activity_err });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        guests: Math.max(1, parseInt(form.guests, 10) || 1),
        activity: String(form.activity),
        date: format(date, "yyyy-MM-dd"),
        message: form.message || "",
      };
      await axios.post(`${API}/bookings`, payload);
      setSent(true);
      toast.success(t.booking.success_title, { description: t.booking.success_body });
      setForm({ name: "", email: "", phone: "", guests: "2", activity: "", message: "" });
      setDate(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(" · ")
        : typeof detail === "string"
        ? detail
        : t.booking.error_body;
      console.error("Booking error:", err?.response?.data || err);
      toast.error(t.booking.error_title, { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-28 md:pt-36 pb-24 bg-white">
      <section className="container-x">
        <p className="label-eyebrow">{t.booking.eyebrow}</p>
        <h1 className="h-display mt-6 max-w-3xl text-5xl md:text-7xl font-light text-[#0B192C]">
          {t.booking.title}
        </h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[#475569] font-light">
          {t.booking.subtitle}
        </p>
      </section>

      <section className="container-x mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 card-soft p-8 md:p-10"
          data-testid="booking-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.name}
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={update("name")}
                required
                className="rounded-xl border-black/10 h-12"
                data-testid="booking-name"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.email}
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="rounded-xl border-black/10 h-12"
                data-testid="booking-email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.phone}
              </Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={update("phone")}
                required
                className="rounded-xl border-black/10 h-12"
                data-testid="booking-phone"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="guests" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.guests}
              </Label>
              <Input
                id="guests"
                type="number"
                min="1"
                max="10"
                value={form.guests}
                onChange={update("guests")}
                required
                className="rounded-xl border-black/10 h-12"
                data-testid="booking-guests"
              />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.activity}
              </Label>
              <Select value={form.activity} onValueChange={update("activity")}>
                <SelectTrigger className="rounded-xl border-black/10 h-12" data-testid="booking-activity-trigger">
                  <SelectValue placeholder={t.booking.fields.pick_activity} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {activities.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`booking-activity-${a.id}`}>
                      {a.title[lang]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.date}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "justify-start text-left h-12 rounded-xl border-black/10 font-normal",
                      !date && "text-[#94A3B8]"
                    )}
                    data-testid="booking-date-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : t.booking.fields.pick_date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-[#475569]">
                {t.booking.fields.message}
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={update("message")}
                placeholder={t.booking.fields.message_ph}
                rows={5}
                className="rounded-xl border-black/10"
                data-testid="booking-message"
              />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <p className="text-xs text-[#94A3B8] max-w-sm">
              {t.extras.booking_reply_note}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
              data-testid="booking-submit"
            >
              {submitting ? t.booking.sending : t.booking.submit}
              {!submitting && <ArrowRight size={16} />}
            </button>
          </div>

          {sent && (
            <div
              className="mt-6 flex items-start gap-3 rounded-xl bg-[#F7F5F0] p-4 text-sm text-[#0B192C]"
              data-testid="booking-success"
            >
              <Check size={18} className="mt-0.5" />
              <div>
                <p className="font-semibold">{t.booking.success_title}</p>
                <p className="text-[#475569] font-light">{t.booking.success_body}</p>
              </div>
            </div>
          )}
        </motion.form>

        <aside className="lg:col-span-5 relative overflow-hidden rounded-2xl min-h-[420px]">
          <img
            src="https://images.unsplash.com/photo-1612764324168-7a3a318e0cbb"
            alt="Viksund 340 St. Cruz"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/80 to-[#0B192C]/50" />
          <div className="relative z-10 p-8 md:p-10 h-full flex flex-col justify-end text-white">
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-white" data-testid="booking-charter-label">
              {t.extras.booking_charter_label}
            </p>
            <h3 className="h-display mt-3 text-2xl md:text-3xl font-light text-white">
              {t.extras.booking_charter_title}
            </h3>
            <ul className="mt-6 space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><Check size={16} /> {t.extras.booking_charter_1}</li>
              <li className="flex gap-3"><Check size={16} /> {t.extras.booking_charter_2}</li>
              <li className="flex gap-3"><Check size={16} /> {t.extras.booking_charter_3}</li>
              <li className="flex gap-3"><Check size={16} /> {t.extras.booking_charter_4}</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default Booking;
